/**
 * The outbox: writes made with no signal, sent when there is one.
 *
 * Every queued op is replayed under the id it was minted with, through the
 * same route the live call would have used, so the Worker re-validates all of
 * it and the idempotent middleware makes a second arrival harmless. Order is
 * the order it was typed — the ids are UUIDv7, so the store's key order is the
 * replay order — which matters because a row's second edit carries the
 * rowVersion its first edit produced.
 *
 * What the Worker refuses is dropped and reported, not retried: a 409 means
 * the office moved that row first, a 422 means the day changed under it (an
 * approved section, a missing reason). The clerk sees which change it was and
 * why. Only a request that never arrived is kept for the next attempt.
 *
 * Triggered by the browser's online event, by the first successful request
 * after a spell offline, by every unlock, and every twenty seconds while there
 * is anything to send.
 */

import { ApiError, request, uploadBlob, uuidv7 } from './api';
import { isNetworkError, subscribeConnectivity } from './connectivity';
import { kvGet, kvSet, outboxAll, outboxCount, outboxDelete, outboxPut } from './store';
import type { Op, QueuedOp } from './offline';
import { toWire } from './offline';
import { getSession, reconnect } from './session';

export interface SyncProblem {
  id: string;
  label: string;
  message: string;
  at: string;
}

export interface SyncState {
  queued: number;
  draining: boolean;
  problems: SyncProblem[];
  /** When the queue last emptied — the banner's "sent at 10:42". */
  lastSentAt: string | null;
}

const PROBLEMS_KEY = 'sync-problems';
const RETRY_MS = 20_000;

let state: SyncState = { queued: 0, draining: false, problems: [], lastSentAt: null };
const listeners = new Set<(state: SyncState) => void>();
// "The Worker's view moved — reload what you are showing."
const synced = new Set<() => void>();

function set(patch: Partial<SyncState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener(state);
}

export function subscribeSync(listener: (state: SyncState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => listeners.delete(listener);
}

export function onSynced(listener: () => void): () => void {
  synced.add(listener);
  return () => synced.delete(listener);
}

async function refreshCount(): Promise<void> {
  set({ queued: await outboxCount() });
}

// -------------------------------------------------------------- queue

export async function enqueue(op: Op, label: string, blob?: Blob, contentType?: string): Promise<string> {
  const id = uuidv7();
  const record: QueuedOp = {
    id,
    at: new Date().toISOString(),
    label,
    op,
    ...(blob ? { blob, contentType: contentType ?? 'application/octet-stream' } : {}),
  };
  await outboxPut(id, record);
  await refreshCount();
  return id;
}

export async function queuedFor(date: string): Promise<QueuedOp[]> {
  const all = await outboxAll<QueuedOp>();
  return all.map((row) => row.record).filter((record) => record.op.date === date);
}

// ----------------------------------------------------------- problems

async function addProblem(problem: SyncProblem): Promise<void> {
  const problems = [...state.problems, problem].slice(-20);
  await kvSet(PROBLEMS_KEY, problems);
  set({ problems });
}

export async function dismissProblems(): Promise<void> {
  await kvSet(PROBLEMS_KEY, []);
  set({ problems: [] });
}

// -------------------------------------------------------------- drain

let draining: Promise<void> | null = null;

/** Sends everything queued, in order, stopping at the first request that never arrives. */
export function drain(): Promise<void> {
  if (draining) return draining;
  draining = runDrain().finally(() => { draining = null; });
  return draining;
}

async function runDrain(): Promise<void> {
  if (!getSession()) return;
  const all = await outboxAll<QueuedOp>();
  if (all.length === 0) return;

  set({ draining: true });
  let moved = false;
  try {
    // A session that was granted with no signal has no token; one that was
    // granted before the signal dropped may have expired since. Either way the
    // held credential gets a fresh one, without asking for the PIN again.
    if (!(await reconnect())) return;

    for (const { id, record } of all) {
      const outcome = await sendOne(id, record);
      if (outcome === 'unreachable') break;
      await outboxDelete(id);
      moved = true;
      if (outcome !== 'sent') {
        await addProblem({ id, label: record.label, message: outcome, at: new Date().toISOString() });
      }
    }
  } finally {
    await refreshCount();
    set({ draining: false, ...(moved && state.queued === 0 ? { lastSentAt: new Date().toISOString() } : {}) });
    if (moved) for (const listener of synced) listener();
  }
}

/** 'sent', 'unreachable', or the reason the Worker refused it. */
async function sendOne(id: string, record: QueuedOp, retried = false): Promise<string> {
  const wire = toWire(record.op);
  try {
    if (record.blob) {
      await uploadBlob(wire.path, record.blob, record.contentType ?? 'application/octet-stream', id);
    } else {
      await request(wire.path, { method: wire.method, body: wire.body, clientOpId: id });
    }
    return 'sent';
  } catch (err) {
    if (isNetworkError(err)) return 'unreachable';
    if (err instanceof ApiError && err.needsSignIn && !retried && (await reconnect())) {
      return sendOne(id, record, true);
    }
    if (err instanceof ApiError && err.needsSignIn) return 'unreachable';
    return err instanceof Error ? err.message : 'That change was refused.';
  }
}

// -------------------------------------------------------------- start

/** Wire up the triggers. Called once the app has a session; returns the stop. */
export function startSync(): () => void {
  void kvGet<SyncProblem[]>(PROBLEMS_KEY).then((problems) => set({ problems: problems ?? [] }));
  void refreshCount().then(() => drain());

  const stopConnectivity = subscribeConnectivity((online) => { if (online) void drain(); });
  const timer = setInterval(() => { if (state.queued > 0) void drain(); }, RETRY_MS);

  return () => {
    stopConnectivity();
    clearInterval(timer);
  };
}
